import axios from "axios";
import Cookies from "js-cookie";

const CLIENT_ID_KEY = "fiftyone_client_id";
const SESSION_ACTIVE_KEY = "fiftyone_session_active";
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

let heartbeatTimer = null;

function createClientId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getFiftyOneClientId() {
  let clientId = window.sessionStorage.getItem(CLIENT_ID_KEY);
  if (!clientId) {
    clientId = createClientId();
    window.sessionStorage.setItem(CLIENT_ID_KEY, clientId);
  }
  return clientId;
}

export function getFiftyOneRequestContext() {
  const requesterToken = Cookies.get("user_token");
  if (!requesterToken) {
    throw new Error("Please sign in before opening FiftyOne.");
  }
  return {
    requesterToken,
    client_id: getFiftyOneClientId(),
  };
}

function getFiftyOneBaseUrl(port) {
  const useHost =
    process.env.REACT_APP_INTEGRATED_FIFTYONE_USE_HOST === "1" ||
    process.env.REACT_APP_INTEGRATED_FIFTYONE_USE_HOST === "true";
  const configuredUrl =
    process.env.REACT_APP_INTEGRATED_FIFTYONE_URL ||
    `http://${window.location.hostname}:5151/`;
  const baseUrl = useHost
    ? new URL(`http://${window.location.hostname}/`)
    : new URL(configuredUrl);
  baseUrl.port = String(port);
  baseUrl.pathname = "/";
  baseUrl.search = "";
  baseUrl.hash = "";
  return baseUrl;
}

function getFiftyOneUrl(port, datasetName) {
  const baseUrl = getFiftyOneBaseUrl(port);
  if (datasetName) {
    baseUrl.pathname = `/datasets/${encodeURIComponent(datasetName)}`;
  }
  return baseUrl.toString();
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

async function heartbeat() {
  try {
    await axios.put(
      "/infras/fiftyone/session",
      getFiftyOneRequestContext()
    );
  } catch (error) {
    if (error.response?.status === 401 || error.response?.status === 404) {
      stopHeartbeat();
      window.sessionStorage.removeItem(SESSION_ACTIVE_KEY);
    }
  }
}

function startHeartbeat() {
  window.sessionStorage.setItem(SESSION_ACTIVE_KEY, "1");
  if (!heartbeatTimer) {
    heartbeatTimer = window.setInterval(
      heartbeat,
      HEARTBEAT_INTERVAL_MS
    );
  }
}

function errorMessage(error) {
  const responseData = error.response?.data;
  if (typeof responseData === "string") {
    return responseData;
  }
  return responseData?.error || error.message || "Failed to open FiftyOne.";
}

async function refreshRequesterToken() {
  const requesterToken = Cookies.get("user_token");
  if (!requesterToken) {
    throw new Error("Please sign in before opening FiftyOne.");
  }
  const response = await axios.post("/auth/login", { requesterToken });
  const refreshedToken = response.data?.token;
  if (!refreshedToken) {
    throw new Error("Failed to refresh the login session.");
  }
  Cookies.set("user_token", refreshedToken);
}

async function ensureFiftyOneSession() {
  try {
    return await axios.post(
      "/infras/fiftyone/session",
      getFiftyOneRequestContext()
    );
  } catch (error) {
    if (error.response?.status !== 401) {
      throw error;
    }
    await refreshRequesterToken();
    return axios.post(
      "/infras/fiftyone/session",
      getFiftyOneRequestContext()
    );
  }
}

export async function openFiftyOne(datasetName = null) {
  const popup = window.open("about:blank");
  if (!popup) {
    window.alert("Please allow pop-ups to open FiftyOne.");
    return;
  }

  try {
    const response = await ensureFiftyOneSession();
    const port = Number(response.data?.port);
    if (!Number.isInteger(port)) {
      throw new Error("The FiftyOne session did not return a valid port.");
    }
    popup.location.href = getFiftyOneUrl(port, datasetName);
    startHeartbeat();
  } catch (error) {
    popup.close();
    window.alert(errorMessage(error));
  }
}

export async function releaseFiftyOneSession() {
  stopHeartbeat();
  window.sessionStorage.removeItem(SESSION_ACTIVE_KEY);
  if (!Cookies.get("user_token")) {
    return;
  }
  try {
    await axios.delete("/infras/fiftyone/session", {
      data: getFiftyOneRequestContext(),
      timeout: 2000,
    });
  } catch (error) {
    // The backend idle reaper will release sessions after interrupted logouts.
  }
}

if (window.sessionStorage.getItem(SESSION_ACTIVE_KEY) === "1") {
  startHeartbeat();
}
